import React from 'react';
import { FiBriefcase, FiMapPin, FiUsers } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGridThree,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount
} from '../common/FormStyles';
import { BusinessInfo } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { BUSINESS_INFO_DOCUMENTS } from '@/config/documentsBySection';
import { Candidate } from '@/models';

interface BusinessInfoSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
  readonly?: boolean;
}

export const BusinessInfoSection: React.FC<BusinessInfoSectionProps> = ({
  data,
  onChange,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  // Calcul automatique des années d'existence
  const calculateAnnees = () => {
    if (data.date_creation) {
      const creation = new Date(data.date_creation);
      const maintenant = new Date();
      const diffMs = maintenant.getTime() - creation.getTime();
      const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365);
      const annees = Math.floor(diffYears);

      if (data.annees_existence !== annees) {
        handleChange('annees_existence', annees);
      }
    }
  };

  React.useEffect(() => {
    calculateAnnees();
  }, [data.date_creation]);

  return (
    <FormSection>
      <SectionTitle>
        <FiBriefcase />
        Informations sur l'Entreprise
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Renseignez les informations relatives à votre entreprise. Ces informations sont
          essentielles pour évaluer la viabilité et la stabilité de votre activité.
        </p>
      </InfoBox>

      <SubSectionTitle>Identification de l'Entreprise</SubSectionTitle>

      <FormGroup>
        <Label $required>Nom commercial</Label>
        <Input
          type="text"
          value={data.nom_commercial || ''}
          onChange={e => handleChange('nom_commercial', e.target.value)}
          placeholder="Ex: Boutique Chez Aya, Garage Moderne..."
          disabled={readonly}
        />
        <HelpText>Nom sous lequel votre entreprise est connue</HelpText>
      </FormGroup>

      <FormGroup>
        <Label $required>Type d'activité principale</Label>
        <Input
          type="text"
          value={data.type_activite_principale || ''}
          onChange={e => handleChange('type_activite_principale', e.target.value)}
          placeholder="Ex: Vente de produits cosmétiques, Réparation automobile..."
          disabled={readonly}
        />
      </FormGroup>

      <FormGroup>
        <Label>Activités secondaires</Label>
        <TextArea
          value={data.activites_secondaires || ''}
          onChange={e => handleChange('activites_secondaires', e.target.value)}
          placeholder="Décrivez vos autres activités si vous en avez..."
          disabled={readonly}
        />
        <HelpText>Optionnel - Autres activités que vous exercez</HelpText>
      </FormGroup>

      <SubSectionTitle>Statut Juridique et Documents</SubSectionTitle>

      <FormGroup>
        <Label $required>Statut juridique</Label>
        <Select
          value={data.statut_juridique || ''}
          onChange={e => handleChange('statut_juridique', e.target.value)}
          disabled={readonly}
        >
          <option value="">Sélectionnez...</option>
          <option value="Entreprise individuelle">Entreprise individuelle</option>
          <option value="SARL">SARL</option>
          <option value="SA">SA</option>
          <option value="GIE">GIE</option>
          <option value="Coopérative">Coopérative</option>
          <option value="Établissement">Établissement</option>
          <option value="Non formalisée (en cours)">Non formalisée (en cours)</option>
        </Select>
        <HelpText>
          Sélectionnez "Non formalisée (en cours)" si vous êtes en cours de formalisation
        </HelpText>
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label>Registre de commerce</Label>
          <ToggleSwitch>
            <ToggleInput
              checked={data.registre_commerce || false}
              onChange={e => handleChange('registre_commerce', e.target.checked)}
              disabled={readonly}
            />
            <ToggleLabel>
              {data.registre_commerce ? 'Oui, j\'ai un RCCM' : 'Non, pas encore'}
            </ToggleLabel>
          </ToggleSwitch>
        </FormGroup>

        {data.registre_commerce && (
          <FormGroup>
            <Label>Numéro RCCM</Label>
            <Input
              type="text"
              value={data.numero_rccm || ''}
              onChange={e => handleChange('numero_rccm', e.target.value)}
              placeholder="Ex: CI-ABJ-01-2023-B12-12345"
              disabled={readonly}
            />
          </FormGroup>
        )}
      </FormGrid>

      <FormGroup>
        <Label>Numéro IFU (Identifiant Fiscal Unique)</Label>
        <Input
          type="text"
          value={data.numero_ifu || ''}
          onChange={e => handleChange('numero_ifu', e.target.value)}
          placeholder="Ex: 1234567890123"
          disabled={readonly}
        />
        <HelpText>Si vous avez un numéro IFU, renseignez-le ici</HelpText>
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label>Patente/Licence commerciale</Label>
          <ToggleSwitch>
            <ToggleInput
              checked={data.a_patente || false}
              onChange={e => handleChange('a_patente', e.target.checked)}
              disabled={readonly}
            />
            <ToggleLabel>
              {data.a_patente ? 'Oui, j\'ai une patente' : 'Non, pas encore'}
            </ToggleLabel>
          </ToggleSwitch>
        </FormGroup>

        {data.a_patente && (
          <FormGroup>
            <Label>Numéro de patente</Label>
            <Input
              type="text"
              value={data.numero_patente || ''}
              onChange={e => handleChange('numero_patente', e.target.value)}
              placeholder="Numéro de patente"
              disabled={readonly}
            />
          </FormGroup>
        )}
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label $required>Date de création de l'entreprise</Label>
          <Input
            type="date"
            value={data.date_creation || ''}
            onChange={e => handleChange('date_creation', e.target.value)}
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Années d'existence</Label>
          <Input
            type="number"
            min="0"
            value={data.annees_existence || ''}
            onChange={e => handleChange('annees_existence', parseInt(e.target.value))}
            placeholder="Calculé automatiquement"
            disabled={true}
          />
          <HelpText>Calculé automatiquement à partir de la date de création</HelpText>
        </FormGroup>
      </FormGrid>

      <SubSectionTitle>
        <FiMapPin style={{ fontSize: '18px' }} />
        Localisation de l'Entreprise
      </SubSectionTitle>

      <FormGroup>
        <Label $required>Adresse complète</Label>
        <Input
          type="text"
          value={data.adresse_entreprise || ''}
          onChange={e => handleChange('adresse_entreprise', e.target.value)}
          placeholder="Rue, avenue, numéro..."
          disabled={readonly}
        />
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label $required>Quartier</Label>
          <Input
            type="text"
            value={data.quartier_actuel || ''}
            onChange={e => handleChange('quartier_actuel', e.target.value)}
            placeholder="Nom du quartier"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Marché/Zone</Label>
          <Input
            type="text"
            value={data.marche_zone || ''}
            onChange={e => handleChange('marche_zone', e.target.value)}
            placeholder="Ex: Marché de Cocody, Zone 4C..."
            disabled={readonly}
          />
          <HelpText>Si votre entreprise est située dans un marché</HelpText>
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label $required>Type de local</Label>
          <Select
            value={data.type_local || ''}
            onChange={e => handleChange('type_local', e.target.value)}
            disabled={readonly}
          >
            <option value="">Sélectionnez...</option>
            <option value="Boutique">Boutique</option>
            <option value="Magasin">Magasin</option>
            <option value="Bureau">Bureau</option>
            <option value="Atelier">Atelier</option>
            <option value="Kiosque">Kiosque</option>
            <option value="Container">Container</option>
            <option value="Étal marché">Étal au marché</option>
            <option value="Domicile">À domicile</option>
            <option value="Ambulant">Ambulant</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label $required>Statut du local</Label>
          <Select
            value={data.statut_local || ''}
            onChange={e => handleChange('statut_local', e.target.value)}
            disabled={readonly}
          >
            <option value="">Sélectionnez...</option>
            <option value="Propriétaire">Propriétaire</option>
            <option value="Locataire">Locataire</option>
            <option value="Gratuit">Gratuit (prêt)</option>
          </Select>
        </FormGroup>
      </FormGrid>

      {data.statut_local === 'Locataire' && (
        <FormGroup>
          <Label>Montant du loyer mensuel (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.loyer_local || ''}
            onChange={e => handleChange('loyer_local', parseInt(e.target.value))}
            placeholder="Ex: 50000"
            disabled={readonly}
          />
        </FormGroup>
      )}

      <SubSectionTitle>
        <FiUsers style={{ fontSize: '18px' }} />
        Effectif et Personnel
      </SubSectionTitle>

      <InfoBox $type="warning">
        <p>
          Indiquez le nombre de personnes qui travaillent dans votre entreprise,
          même s'il s'agit de membres de votre famille ou d'apprentis non rémunérés.
        </p>
      </InfoBox>

      <FormGridThree>
        <FormGroup>
          <Label>Employés permanents</Label>
          <Input
            type="number"
            min="0"
            value={data.nombre_employes_permanents || ''}
            onChange={e => handleChange('nombre_employes_permanents', parseInt(e.target.value))}
            placeholder="0"
            disabled={readonly}
          />
          <HelpText>Employés avec salaire régulier</HelpText>
        </FormGroup>

      

        <FormGroup>
          <Label>Travailleurs occasionnels</Label>
          <Input
            type="number"
            min="0"
            value={data.nombre_journaliers || ''}
            onChange={e => handleChange('nombre_journaliers', parseInt(e.target.value))}
            placeholder="0"
            disabled={readonly}
          />
          <HelpText>Employés avec salaire irrégulier </HelpText>
        </FormGroup>
      </FormGridThree>

      

      {((data.nombre_employes_permanents || 0) +
        (data.nombre_journaliers || 0)) > 0 && (
        <CalculatedValue>
          <CalculatedLabel>Total de personnes travaillant dans l'entreprise</CalculatedLabel>
          <CalculatedAmount>
            {(data.nombre_employes_permanents || 0) +
              (data.nombre_journaliers || 0)}{' '}
            personne(s)
          </CalculatedAmount>
        </CalculatedValue>
      )}

      {/* Documents justificatifs pour cette section */}
      {onDocumentUpload && onDocumentRemove && (
        <DocumentUpload
          sectionTitle="Informations de l'Entreprise"
          documents={BUSINESS_INFO_DOCUMENTS}
          uploadedFiles={uploadedDocuments}
          onFileSelect={onDocumentUpload}
          onFileRemove={onDocumentRemove}
          readonly={readonly}
        />
      )}
    </FormSection>
  );
};
